import { auth, functions } from './firebase';
import {
  onAuthStateChanged as onAuthStateChangedSDK,
  onIdTokenChanged as onIdTokenChangedSDK,
  getIdToken as getIdTokenSDK,
  signInWithEmailAndPassword as signInWithEmailAndPasswordSDK,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { linkUserToEmployee as linkUserToEmployeeApi } from '@/domain/employees/apiRest';

export type { User };

// Cloud Function Response Types
interface CreateJoinRequestResponse {
  success: boolean;
  requestId: string;
  status: 'pending';
  expiresAt: number;
  message: string;
}

interface GetJoinRequestStatusResponse {
  hasRequest: boolean;
  requestId?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'expired';
  requestedAt?: number;
  expiresAt?: number;
  reviewedAt?: number;
  rejectionReason?: string;
}

export const onAuthStateChanged = (cb: (u: User | null) => void) =>
  onAuthStateChangedSDK(auth, cb);

export const onIdTokenChanged = (cb: (u: User | null) => void) =>
  onIdTokenChangedSDK(auth, cb);

// Force refresh only when explicitly needed (e.g., after claims change).
// DO NOT use this inside onIdTokenChanged listeners — it creates infinite loops.
export const getIdToken = (u: User, forceRefresh = false) => getIdTokenSDK(u, forceRefresh);

export const signInWithEmailAndPassword = (email: string, password: string) =>
  signInWithEmailAndPasswordSDK(auth, email, password);

export async function joinCompany(input: { companyDomain?: string; companyName?: string }) {
  const res = await linkUserToEmployeeApi(input);
  // force claims refresh
  await auth.currentUser?.getIdToken(true);
  return res;
}

// Join Request System
export async function createJoinRequest(input: {
  tenantId: string;
  userName: string;
  userEmail: string;
  companySearchQuery?: string;
}) {
  const fn = httpsCallable<typeof input, CreateJoinRequestResponse>(functions, 'createJoinRequest');
  const res = await fn(input);
  return res.data;
}

export async function getJoinRequestStatus(tenantId: string) {
  const fn = httpsCallable<{ tenantId: string }, GetJoinRequestStatusResponse>(functions, 'getJoinRequestStatus');
  const res = await fn({ tenantId });
  return res.data;
}

export async function updateUserPassword(currentPassword: string, newPassword: string) {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No authenticated user found');
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  } catch (error) {
    throw error;
  }
}

// Re-export selected Firebase Auth SDK helpers for adapter consumers
export {
  getAuth,
  connectAuthEmulator,
  setPersistence,
  inMemoryPersistence,
  signOut,
  signInWithCustomToken,
} from 'firebase/auth';

// Also expose the SDK sign-in under an explicit name to avoid clashing
export { signInWithEmailAndPassword as signInWithEmailAndPasswordSDK } from 'firebase/auth';
